// Phase 37 · 23a — the Board header cluster, in the theme.
//
// Repaint only. Every control, prop and handler is the one that was here
// before: title + count, pipeline selector, search, My Tickets, Filters (and
// its floating AdvancedFilters panel), Export, the view toggle, Refresh, New
// Ticket and the PageHeaderMeta utility cluster. Nothing was added or removed.
//
// The one behavioural nicety: the view toggle is now a two-option segmented
// control rather than a single swap button, so which view you are looking at
// is visible instead of inferred. Clicking the already-active half is a no-op;
// clicking the other half calls the same `onViewToggle` as before.
//
// This component is used ONLY by the Board (the Tickets register builds its
// own header), so the repaint reaches exactly one screen.
import { useState, useRef, useCallback } from 'react';
import {
  Search,
  SlidersHorizontal,
  RotateCw,
  List,
  LayoutGrid,
  X,
  UserCheck,
} from 'lucide-react';
import { clsx } from 'clsx';
import { useAuthStore } from '@stores/authStore';
import { AdvancedFilters, type TicketFilters } from '@components/tickets/AdvancedFilters';
import { ExportButton } from '@components/common/ExportButton';
import { PageHeaderMeta } from '@components/layouts/PageHeaderMeta';
import type { ExportColumn } from '@utils/export';
import type { Pipeline } from '@/types/pipeline';
import { PipelineSelect } from '@components/tickets/PipelineSelect';

/** The floating shell: the header sits ON the canvas, it does not butt against it. */
const CARD: React.CSSProperties = { borderRadius: 14, boxShadow: 'var(--co-shadow-card)' };

const CONTROL =
  'flex h-[34px] flex-none items-center gap-[7px] whitespace-nowrap rounded-[10px] border ' +
  'border-[var(--co-line-strong)] bg-[var(--co-raised)] px-3 text-[12.5px] ' +
  'text-[var(--co-ink-2)] hover:border-[var(--co-brass)] hover:bg-[var(--co-brass-wash)] ' +
  'disabled:cursor-not-allowed disabled:opacity-50';
const CONTROL_ON = 'border-[var(--co-brass)] bg-[var(--co-brass-wash)] text-[var(--co-brass-ink)]';
const PRIMARY =
  'flex h-[34px] flex-none items-center rounded-[10px] bg-[var(--co-brass-ink)] px-[14px] ' +
  'text-[12.5px] font-bold text-[var(--co-brass-on)] hover:opacity-90';

interface TicketToolbarProps {
  // Identity
  title: string;
  ticketCount: number;

  // Search
  searchQuery: string;
  onSearchChange: (query: string) => void;
  // Receives the value to commit. The toolbar passes the current input on
  // form submit and an empty string on clear, so the parent never has to
  // reach for a possibly-stale `searchQuery` closure.
  onSearchSubmit: (value: string) => void;

  // Filters
  filters: TicketFilters;
  onFiltersChange: (filters: TicketFilters) => void;
  activeFilterCount: number;

  // Pipeline selector (board + list)
  pipelines?: Pipeline[];
  selectedPipelineId?: string | null;
  onPipelineChange?: (id: string) => void;
  /** Prepend an "All Pipelines" option (value 'all'). List view only. */
  allowAllPipelines?: boolean;

  // Export
  onExportData?: () => Promise<any[]>;
  exportColumns?: ExportColumn<any>[];

  // Navigation
  viewMode: 'board' | 'list';
  onViewToggle: () => void;

  // Actions
  onRefresh: () => void;
  onNewTicket: () => void;
  isLoading?: boolean;
}

export function TicketToolbar({
  title,
  ticketCount,
  searchQuery,
  onSearchChange,
  onSearchSubmit,
  filters,
  onFiltersChange,
  activeFilterCount,
  pipelines,
  selectedPipelineId,
  onPipelineChange,
  allowAllPipelines = false,
  onExportData,
  exportColumns,
  viewMode,
  onViewToggle,
  onRefresh,
  onNewTicket,
  isLoading = false,
}: TicketToolbarProps) {
  const [showFilters, setShowFilters] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  const currentUserId = user?.id;

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      onSearchSubmit(searchQuery);
    },
    [onSearchSubmit, searchQuery]
  );

  const handleClearSearch = useCallback(() => {
    onSearchChange('');
    onSearchSubmit('');
  }, [onSearchChange, onSearchSubmit]);

  return (
    // Sticky so the header stays pinned while the board scrolls underneath,
    // and the positioning anchor for the filter overlay.
    <header
      className="relative z-30 min-w-0 flex-none border border-[var(--co-line)] bg-[var(--co-raised)] px-[18px] py-4"
      style={CARD}
    >
      {/* Row 1: title + count on the left, the utility cluster (connection +
          alerts + mobile drawer trigger) in the top-right corner. */}
      <div className="flex items-center justify-between gap-[10px]">
        <div className="flex min-w-0 items-baseline gap-[9px]">
          <h1 className="text-[25px] font-bold leading-[1.15] tracking-[-.018em] text-[var(--co-ink)]">
            {title}
          </h1>
          {ticketCount > 0 && (
            <span className="co-mono rounded-full bg-[var(--co-selected)] px-[9px] py-[3px] text-[11px] font-semibold tabular-nums text-[var(--co-ink-2)]">
              {ticketCount}
            </span>
          )}
        </div>
        <div className="flex h-[34px] flex-none items-center">
          <PageHeaderMeta />
        </div>
      </div>

      {/* Row 2: every other control, wrapping when space runs out. */}
      <div className="mt-3 flex items-start gap-[10px] border-t border-[var(--co-line-subtle)] pt-3">
        {/* Filters wrap among themselves on the left; the actions keep the
            top-right corner at every width. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-[10px]">
        {/* Pipeline selector */}
        {pipelines && pipelines.length > 0 && onPipelineChange && (
          <PipelineSelect
            value={selectedPipelineId || (allowAllPipelines ? 'all' : '')}
            onChange={onPipelineChange}
            options={[
              ...(allowAllPipelines ? [{ value: 'all', label: 'All Pipelines' }] : []),
              ...pipelines.map((pipeline) => ({ value: pipeline.id, label: pipeline.name })),
            ]}
            className={clsx(
              CONTROL,
              'border-[var(--co-brass)] font-semibold text-[var(--co-ink)]'
            )}
          />
        )}

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="relative min-w-0 max-w-[280px] flex-[1_1_180px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-[13px] w-[13px] -translate-y-1/2 text-[var(--co-ink-3)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search tickets…"
            className="h-[34px] w-full rounded-[10px] border border-[var(--co-line-strong)] bg-[var(--co-raised)] pl-[30px] pr-8 text-[12.5px] text-[var(--co-ink)] placeholder:text-[var(--co-ink-3)] focus:border-[var(--co-brass)] focus:outline-none"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={handleClearSearch}
              aria-label="Clear search"
              className="absolute right-[9px] top-1/2 -translate-y-1/2 text-[var(--co-ink-3)] hover:text-[var(--co-ink)]"
            >
              <X className="h-[13px] w-[13px]" />
            </button>
          )}
        </form>

        {/* The free space sits between the search and the actions. */}
        <div className="flex-1" />

        </div>
        <div className="flex flex-none items-center gap-[10px]">

        {/* My Tickets — one-click "assigned to me" quick filter, for every
            role. Applies immediately (no panel); overrides any assignee
            selection and shows ONLY the current user's tickets. */}
        {currentUserId && (
          <button
            type="button"
            onClick={() =>
              onFiltersChange(
                filters.assignedToMe
                  ? { ...filters, assignedToMe: undefined }
                  : { ...filters, assignedToMe: true, assigneeIds: undefined },
              )
            }
            aria-pressed={!!filters.assignedToMe}
            title="Show only tickets assigned to me"
            className={clsx(CONTROL, filters.assignedToMe && CONTROL_ON)}
          >
            <UserCheck className="h-[14px] w-[14px]" />
            <span className="hidden sm:inline">My Tickets</span>
          </button>
        )}

        {/* Filters + its floating panel */}
        <div className="relative flex-none" ref={filterPanelRef}>
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(CONTROL, (showFilters || activeFilterCount > 0) && CONTROL_ON)}
          >
            <SlidersHorizontal className="h-[14px] w-[14px]" />
            <span className="hidden sm:inline">Filters</span>
            {activeFilterCount > 0 && (
              <span className="co-mono text-[10px] font-semibold text-[var(--co-brass-ink)]">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* The panel floats over the board rather than pushing it down. It
              closes on Apply / Reset / ✕ (AdvancedFilters calls onClose). */}
          {showFilters && (
            <div className="absolute right-0 top-full z-50 mt-2 w-[min(92vw,44rem)]">
              <AdvancedFilters
                filters={filters}
                onChange={onFiltersChange}
                onClose={() => setShowFilters(false)}
                currentUserId={currentUserId}
              />
            </div>
          )}
        </div>

        {/* Export */}
        {onExportData && exportColumns && (
          <ExportButton
            onFetchData={onExportData}
            columns={exportColumns}
            filename="tickets"
            sheetName="Tickets"
            variant="ghost"
            size="sm"
            className={`${CONTROL} !rounded-[10px]`}
          />
        )}

        {/* View toggle — which view you are in is shown, not inferred. */}
        <div className="flex flex-none items-center gap-1 rounded-[10px] bg-[var(--co-selected)] p-[3px]">
          {(['board', 'list'] as const).map((mode) => {
            const active = viewMode === mode;
            const Icon = mode === 'board' ? LayoutGrid : List;
            return (
              <button
                key={mode}
                type="button"
                onClick={() => { if (!active) onViewToggle(); }}
                aria-pressed={active}
                title={mode === 'board' ? 'Board view' : 'List view'}
                className={clsx(
                  'flex h-[28px] w-[28px] items-center justify-center rounded-lg',
                  active
                    ? 'bg-[var(--co-raised)] text-[var(--co-ink)] shadow-[0_1px_2px_rgba(35,32,28,.08)]'
                    : 'text-[var(--co-ink-3)] hover:text-[var(--co-ink)]'
                )}
              >
                <Icon className="h-[14px] w-[14px]" />
              </button>
            );
          })}
        </div>

        {/* Refresh */}
        <button
          type="button"
          onClick={onRefresh}
          disabled={isLoading}
          aria-label="Refresh"
          className={clsx(CONTROL, 'px-[10px]')}
        >
          <RotateCw className={clsx('h-[14px] w-[14px]', isLoading && 'animate-spin')} />
        </button>

        {/* New Ticket */}
        <button type="button" onClick={onNewTicket} className={PRIMARY}>
          <span className="sm:hidden">+</span>
          <span className="hidden sm:inline">New Ticket</span>
        </button>
        </div>
      </div>
    </header>
  );
}
